import * as React from 'react';

import { useThemeStore } from '../theme/theme.store';
import { usePlatformGeneral } from './use-platform-general';

/**
 * The logo and browser icon for the theme on screen.
 *
 * A mark drawn for a light background usually disappears on a dark one, so
 * each has an optional dark variant. An empty variant falls back to the single
 * image rather than to nothing — one logo that works in both themes stays one
 * setting, and only a brand that needs two has to supply two.
 */
export type BrandDisplay = 'both' | 'logo' | 'name';

export interface SocietyBrand {
  logoUrl?: string | null;
  logoUrlDark?: string | null;
  faviconUrl?: string | null;
  faviconUrlDark?: string | null;
  brandDisplay?: BrandDisplay | null;
  logoHeight?: number | null;
}

/**
 * What the sidebar shows: the society's own choice where it has made one,
 * otherwise the platform's. A society that has never touched the setting
 * follows the platform, which is how every one of them starts.
 */
/**
 * Sidebar logo height in pixels, as the society set it, else the platform, else
 * the 32px the console has always drawn. Clamped because the header is 56px
 * tall and a taller logo would push against its edges.
 */
export function useBrandLogoHeight(society?: SocietyBrand | null): number {
  const general = usePlatformGeneral();
  const chosen = society?.logoHeight ?? general?.consoleLogoHeight ?? 32;
  return Math.min(48, Math.max(16, chosen));
}

export function useBrandDisplay(society?: SocietyBrand | null): BrandDisplay {
  const general = usePlatformGeneral();
  return society?.brandDisplay ?? general?.consoleBrandDisplay ?? 'both';
}

export function useBrandAssets(society?: SocietyBrand | null): {
  logo: string;
  favicon: string;
  /**
   * Whether the logo above is this society's own rather than the platform's.
   *
   * It matters because "Logo only" is safe exactly when the mark identifies the
   * society. A society that has never uploaded one shows the platform's, and
   * hiding the name beside *that* makes every console look the same.
   */
  logoIsOwn: boolean;
} {
  const general = usePlatformGeneral();
  const resolved = useThemeStore((state) => state.resolved);
  const dark = resolved === 'dark';

  // A society's own branding wins over the platform's wherever it has been
  // set — a resident's committee is the brand they recognise, not ours — and
  // each falls back on its own, so a society with a logo but no icon of its
  // own keeps the platform's icon rather than losing both.
  const own = (light?: string | null, darkVariant?: string | null) =>
    (dark ? darkVariant : '') || light || '';
  const theirs = (light?: string, darkVariant?: string) => (dark ? darkVariant : '') || light || '';

  const ownLogo = own(society?.logoUrl, society?.logoUrlDark);

  return {
    logo: ownLogo || theirs(general?.logoUrl, general?.logoUrlDark),
    favicon:
      own(society?.faviconUrl, society?.faviconUrlDark) ||
      theirs(general?.faviconUrl, general?.faviconUrlDark),
    logoIsOwn: Boolean(ownLogo),
  };
}

/**
 * Puts the platform's own name and icon on the browser tab.
 *
 * `index.html` ships a title and a favicon so the first paint is not blank,
 * but they are build-time constants — renaming the product used to mean
 * editing the HTML and rebuilding. These come from General settings instead,
 * so a rename reaches both consoles within the cache window and without a
 * deploy. An empty setting leaves the bundled icon in place rather than
 * clearing it, which is what makes this safe to ship switched on.
 *
 * Called once per shell, beside `useDateFormatSetting`, so the two consoles
 * share the one `site-config` fetch.
 */
export function useBranding(suffix?: string, society?: SocietyBrand | null): void {
  const general = usePlatformGeneral();
  const { favicon } = useBrandAssets(society);
  const siteName = general?.siteName;

  React.useEffect(() => {
    if (!siteName) return;
    document.title = suffix ? `${siteName} · ${suffix}` : siteName;
  }, [siteName, suffix]);

  React.useEffect(() => {
    if (!favicon) return;
    // Reuse the tag from index.html when it is there; some browsers keep the
    // first icon they saw if a second link is simply appended.
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    // The stored icon may be any format, so the build-time `image/svg+xml`
    // would be a lie; letting the browser sniff it is the honest option.
    link.removeAttribute('type');
    link.href = favicon;
  }, [favicon]);
}
